import React, { useEffect, useState, useMemo } from 'react';
import { Table, Card, Spin, DatePicker, Row, Col, Typography, Alert, InputNumber } from 'antd';
import moment from 'moment';
import { getKpi, getDailyReport } from '../api';
import { TZ } from '../../../utils/tz';

const { Text } = Typography;

const DailyReport = ({ dateRange, managers, onSaveManualFact, isAdmin, refreshKey }) => {
    const [loading, setLoading] = useState(false);
    const [dailyData, setDailyData] = useState({});
    const [editingCell, setEditingCell] = useState(null); // { date, managerId, metricKey }
    const [editValue, setEditValue] = useState(null);
    const [savingCells, setSavingCells] = useState(new Set());

    const fetchData = async (silent = false) => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return;
        if (!silent) setLoading(true);
        // Не очищаем данные, чтобы избежать прыжков интерфейса

        const start = dateRange[0].clone().tz(TZ).startOf('day');
        const end = dateRange[1].clone().tz(TZ).endOf('day');

        try {
            const from = start.toISOString();
            const to = end.toISOString();

            const reportData = await getDailyReport({ from, to });

            if (reportData) {
                setDailyData(reportData);
            }
        } catch (e) {
            console.error("Failed to fetch daily report", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(!!refreshKey);
        setEditingCell(null);
    }, [dateRange, refreshKey]);

    const handleSave = async (managerId, date, metricKey, newVal) => {
        if (newVal === null || newVal === undefined) {
            setEditingCell(null);
            return;
        }

        const cellKey = `${date}_${managerId}_${metricKey}`;
        setSavingCells(prev => new Set(prev).add(cellKey));
        setEditingCell(null);

        try {
            await onSaveManualFact(managerId, date, { [metricKey]: newVal });

            // Локальное обновление для мгновенного отображения (Optimistic Update)
            setDailyData(prev => {
                const next = { ...prev };
                const dayArray = [...(next[date] || [])];
                const idx = dayArray.findIndex(d => String(d.id) === String(managerId));

                const newStats = {
                    ...(idx > -1 ? dayArray[idx] : {}),
                    id: managerId,
                    [metricKey]: newVal,
                    [`${metricKey}Manual`]: true
                };

                if (idx > -1) dayArray[idx] = newStats;
                else dayArray.push(newStats);

                next[date] = dayArray;
                return next;
            });
        } catch (e) {
            console.error("Failed to save inline fact", e);
        } finally {
            setSavingCells(prev => {
                const next = new Set(prev);
                next.delete(cellKey);
                return next;
            });
        }
    };

    // Generate rows for the table
    const tableData = useMemo(() => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return [];

        const rows = [];
        const start = dateRange[0].clone().tz(TZ).startOf('day');
        const end = dateRange[1].clone().tz(TZ).endOf('day');
        const current = start.clone();

        // Sum row accumulator
        const sumRow = { key: 'sum', date: 'Сумма', isSummary: true };

        // Индексируем данные для мгновенного поиска по дате и менеджеру
        const dailyMap = new Map(); // Date -> Map<ManagerId, Stats>
        Object.entries(dailyData || {}).forEach(([date, managerList]) => {
            const mgrMap = new Map();
            (managerList || []).forEach(m => mgrMap.set(String(m.id), m));
            dailyMap.set(date, mgrMap);
        });

        while (current.isSameOrBefore(end)) {
            const dateStr = current.format('DD.MM');
            const keyDate = current.format('YYYY-MM-DD');
            const dayManagersMap = dailyMap.get(keyDate);

            const row = {
                key: keyDate,
                date: dateStr,
            };

            managers.forEach(m => {
                const stats = dayManagersMap?.get(String(m.id)) || {};
                row[m.id] = stats;

                // Accumulate sums
                if (!sumRow[m.id]) sumRow[m.id] = { leads: 0, qualified: 0, measure: 0, salesCount: 0, salesSum: 0 };
                sumRow[m.id].leads += Number(stats.leads || 0);
                sumRow[m.id].qualified += Number(stats.qualified || 0);
                sumRow[m.id].measure += Number(stats.measure || 0);
                sumRow[m.id].salesCount += Number(stats.salesCount || 0);
                sumRow[m.id].salesSum += Number(stats.salesSum || 0);
            });

            rows.push(row);
            current.add(1, 'days');
        }

        return { rows, sumRow };
    }, [dailyData, dateRange, managers]);

    // Helper to extract first name (assuming format "Surname Name")
    const getFirstName = (fullName) => {
        if (!fullName) return '';
        const parts = fullName.trim().split(/\s+/);
        // If multiple parts, return the second one (usually name in "Surname Name"), otherwise first
        return parts.length > 1 ? parts[1] : parts[0];
    };

    const getColumns = (metricKey, isMoney = false) => {
        const cols = [
            {
                title: '',
                dataIndex: 'date',
                key: 'date',
                fixed: 'left',
                width: 60,
                render: (text, record) => record.isSummary ? <b>{text}</b> : text
            }
        ];

        const sortedManagers = [...managers].sort((a, b) => {
            const order = ['жасулан', 'алибек', 'санат', 'арай', 'алишер', 'мариям'];
            const getIndex = (name) => {
                const lower = (name || '').toLowerCase();
                const index = order.findIndex(key => lower.includes(key));
                return index === -1 ? 999 : index;
            };
            return getIndex(a.name) - getIndex(b.name);
        });

        sortedManagers.forEach(m => {
            cols.push({
                title: <span style={{ fontSize: '11px' }}>{getFirstName(m.name)}</span>,
                dataIndex: m.id,
                key: m.id,
                width: 70,
                align: 'center',
                render: (stats, record) => {
                    const managerId = m.id;
                    const date = record.key;
                    const isEditing = editingCell?.date === date && editingCell?.managerId === managerId && editingCell?.metricKey === metricKey;
                    const cellKey = `${date}_${managerId}_${metricKey}`;
                    const isSaving = savingCells.has(cellKey);

                    const val = record.isSummary ? record[m.id]?.[metricKey] : stats?.[metricKey];
                    const isManual = !record.isSummary && stats?.[metricKey + 'Manual'];

                    if (isEditing) {
                        return (
                            <InputNumber
                                size="small"
                                autoFocus
                                value={editValue}
                                onChange={setEditValue}
                                style={{ width: '100%', fontSize: '11px' }}
                                onBlur={() => handleSave(managerId, date, metricKey, editValue)}
                                onPressEnter={() => handleSave(managerId, date, metricKey, editValue)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') setEditingCell(null);
                                }}
                            />
                        );
                    }

                    if (isSaving) {
                        return <Spin size="small" />;
                    }

                    if (val === undefined || val === null || val === 0) {
                        return (
                            <div
                                style={{ cursor: isAdmin && !record.isSummary ? 'pointer' : 'default', padding: '4px', minHeight: '24px' }}
                                onDoubleClick={() => {
                                    if (!record.isSummary && isAdmin) {
                                        setEditingCell({ date, managerId, metricKey });
                                        setEditValue(0);
                                    }
                                }}
                            >
                                <span style={{ color: '#d9d9d9', fontSize: '10px' }}>-</span>
                            </div>
                        );
                    }

                    return (
                        <div
                            className={isManual ? 'manual-fact-cell' : ''}
                            style={{
                                fontSize: '11px',
                                cursor: isAdmin && !record.isSummary ? 'pointer' : 'default',
                                padding: '4px',
                                backgroundColor: isManual ? '#fffbe6' : 'transparent',
                                borderRadius: '4px',
                                minHeight: '24px'
                            }}
                            onDoubleClick={() => {
                                if (!record.isSummary && isAdmin) {
                                    setEditingCell({ date, managerId, metricKey });
                                    setEditValue(val);
                                }
                            }}
                        >
                            {isMoney
                                ? new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(val)
                                : val}
                        </div>
                    );
                }
            });
        });

        return cols;
    };

    if (!dateRange) return <Alert message="Выберите период" />;

    const { rows, sumRow } = tableData;
    const dataSource = [...rows, sumRow];

    return (
        <Spin spinning={loading} tip="Загрузка данных...">
            <Row gutter={[16, 16]}>

                <Col xs={24} lg={12}>
                    <Card title="Лиды" size="small" bodyStyle={{ padding: 0 }} headStyle={{ minHeight: '36px', fontSize: '14px' }}>
                        <Table
                            dataSource={dataSource}
                            columns={getColumns('leads')}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            size="small"
                            bordered
                            style={{ fontSize: '11px' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card title="Замеры" size="small" bodyStyle={{ padding: 0 }} headStyle={{ minHeight: '36px', fontSize: '14px' }}>
                        <Table
                            dataSource={dataSource}
                            columns={getColumns('measure')}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            size="small"
                            bordered
                            style={{ fontSize: '11px' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card title="Продажи (кол-во)" size="small" bodyStyle={{ padding: 0 }} headStyle={{ minHeight: '36px', fontSize: '14px' }}>
                        <Table
                            dataSource={dataSource}
                            columns={getColumns('salesCount')}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            size="small"
                            bordered
                            style={{ fontSize: '11px' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card title="Продажи (сумма)" size="small" bodyStyle={{ padding: 0 }} headStyle={{ minHeight: '36px', fontSize: '14px' }}>
                        <Table
                            dataSource={dataSource}
                            columns={getColumns('salesSum', true)}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            size="small"
                            bordered
                            style={{ fontSize: '11px' }}
                        />
                    </Card>
                </Col>

            </Row>
        </Spin>
    );
};

export default DailyReport;
